import {
  useQuery,
  useMutation,
  useQueryClient,
  useIsMutating,
} from "react-query";
import { request } from "../../../utilities/fetch.js";

export const useFetchAccounts = ({ callback }) => {
  const isMutating = useIsMutating({ mutationKey: "adminAccounts" });
  return useQuery(
    "adminAccounts",
    () => {
      return request({
        url: "/admin/accounts",
      });
    },
    {
      refetchInterval: false,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      select: (data) => {
        return data?.data?.sort((a, b) =>
          a.description
            ?.toLowerCase()
            .localeCompare(b.description?.toLowerCase())
        );
      },
      initialData: () => {
        if (localStorage.getItem("accounts")) {
          return {
            data: JSON.parse(localStorage.getItem("accounts")),
          };
        }
      },
      onSuccess: (data) => {
        localStorage.setItem("accounts", JSON.stringify(data));
        if (callback && typeof callback != "undefined") callback(data);
      },
      enabled: isMutating === 0,
    }
  );
};

export const useFetchAccount = ({ account = null, callback, retry = true }) => {
  const isMutating = useIsMutating({ mutationKey: "adminAccounts" });
  return useQuery(
    ["account", account],
    () => {
      if (!account || account === "app") {
        return Promise.resolve({});
      }
      return request({
        url: `/account/${account}`,
      });
    },
    {
      refetchInterval: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: retry,
      select: (data) => {
        return data?.data;
      },
      onSuccess: (data) => {
        if (callback && typeof callback != "undefined") callback(data);
      },
      enabled: isMutating === 0,
    }
  );
};

export const useEditAccount = ({ account = null, callback }) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data) => {
      let thisAccount = account || data?.account;
      data.account = data.clientRef;
      delete data.clientRef;
      return request({
        url: `/admin/accounts/${thisAccount}`,
        method: "patch",
        data: data,
        json: true,
      });
    },
    {
      mutationKey: "adminAccounts",
      onMutate: async (data) => {
        let thisAccount = account || data?.account;
        await queryClient.cancelQueries(["account", thisAccount]);
        // optimistic update data
        const currentData = queryClient.getQueryData([
          "account",
          thisAccount,
        ])?.data;
        if (currentData?.settings) {
          const dbTabs = currentData?.settings?.dbTabs || {};
          data.settings.dbTabs = {
            ...dbTabs,
            ...data?.settings?.dbTabs,
          };
        }

        await queryClient.setQueryData(["account", thisAccount], {
          data: {
            ...currentData,
            ...data,
          },
        });
        queryClient.invalidateQueries(["account", thisAccount]);

        // update 'accounts' array
        await queryClient.cancelQueries("adminAccounts");
        const updated = queryClient.getQueryData("adminAccounts")?.data;
        const changedAccountIndex = updated?.findIndex(
          (account) => account.account === thisAccount
        );
        if (changedAccountIndex >= 0) {
          updated[changedAccountIndex] = {
            ...updated[changedAccountIndex],
            ...data,
          };
          await queryClient.setQueryData("adminAccounts", {
            data: [...updated],
          });
        }
        queryClient.invalidateQueries("adminAccounts");
      },
      onSettled: (data) => {
        if (callback && typeof callback != "undefined") callback(data);
      },
    }
  );
};

export const useCreateAccount = ({ callback }) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data) => {
      return request({
        url: `/admin/accounts`,
        method: "post",
        data: data,
        json: true,
      });
      return false;
    },
    {
      mutationKey: "adminAccounts",
      onMutate: async (_d, _u, data) => {
        // update 'accounts' array
        await queryClient.cancelQueries("adminAccounts");
        const updated = queryClient.getQueryData("adminAccounts")?.data;
        updated.push({ ...data });
        await queryClient.setQueryData("adminAccounts", {
          data: [...updated],
        });
        queryClient.invalidateQueries("adminAccounts");
      },
      onSettled: (data) => {
        if (callback && typeof callback != "undefined") callback(data);
      },
    }
  );
};

export const useDeleteAccount = ({ callback }) => {
  const queryClient = useQueryClient();
  return useMutation(
    (accountName) => {
      return request({
        url: `/admin/accounts/${accountName}`,
        method: "delete",
      });
    },
    {
      mutationKey: "adminAccounts",
      onMutate: async (accountName) => {
        await queryClient.cancelQueries("account");
        const currentAccounts = queryClient.getQueryData("adminAccounts");
        // optimistic update data
        if (currentAccounts?.data?.length > 0) {
          const updated = [...currentAccounts.data];
          const deleteIndex = updated.findIndex(
            (account) => account?.account === accountName
          );
          updated.splice(deleteIndex, 1);
          await queryClient.setQueryData("adminAccounts", {
            data: [...updated],
          });
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries("adminAccounts");
      },
      onSuccess: callback,
    }
  );
};

export const useAddAccount = () => {};

export default useFetchAccounts;
